import { Action, combineReducers, configureStore, ThunkAction } from '@reduxjs/toolkit';
import adaptersSlice from 'ducks/adapters/slices';
import candleValidationsSlice from 'ducks/candle-validations/slices';
import candleWorkersSlice from 'ducks/candle-workers/slices';
import candlesSlice from 'ducks/candles/slices';
import contractTypesSlice from 'ducks/contract-types/slices';
import currenciesSlice from 'ducks/currencies/slices';
import expiryMethodsSlice from 'ducks/expiry-methods/slices';
import indicatorsSlice from 'ducks/indicators/slices';
import modalsSlice from 'ducks/modals/slices';
import operatorsSlice from 'ducks/operators/slices';
import overlaysSlice from 'ducks/overlays/slices';
import pairSlice from 'ducks/pair/slices';
import pairsSlice from 'ducks/pairs/slices';
import popoversSlice from 'ducks/popovers/slices';
import settingsSlice from 'ducks/settings/slices';
import signalSlice from 'ducks/signal/slices';
import sizingMethodsSlice from 'ducks/sizing-methods/slices';
import statusesSlice from 'ducks/statuses/slices';
import strategiesSlice from 'ducks/strategies/slices';
import strategySlice from 'ducks/strategy/slices';
import strikeMethodsSlice from 'ducks/strike-methods/slices';
import triggerTypesSlice from 'ducks/trigger-types/slices';
import userSlice from 'ducks/user/slices';
import {
  FLUSH,
  PAUSE,
  PERSIST,
  persistReducer,
  persistStore,
  PURGE,
  REGISTER,
  REHYDRATE,
} from 'redux-persist';
import storage from 'redux-persist/lib/storage';

const persistConfig = {
  key: 'root',
  version: 1,
  storage,
  whitelist: ['user'],
};

const appReducer = combineReducers({
  user: userSlice,
  settings: settingsSlice,
  modals: modalsSlice,
  popovers: popoversSlice,
  overlays: overlaysSlice,
  pair: pairSlice,
  pairs: pairsSlice,
  adapters: adaptersSlice,
  signal: signalSlice,
  strategy: strategySlice,
  strategies: strategiesSlice,
  currencies: currenciesSlice,
  indicators: indicatorsSlice,
  candles: candlesSlice,
  candle_workers: candleWorkersSlice,
  candle_validations: candleValidationsSlice,
  statuses: statusesSlice,
  contract_types: contractTypesSlice,
  expiry_methods: expiryMethodsSlice,
  sizing_methods: sizingMethodsSlice,
  strike_methods: strikeMethodsSlice,
  operators: operatorsSlice,
  trigger_types: triggerTypesSlice,
});

// Define root reducer with reset functionality
const rootReducer = (state: RootState | undefined, action: Action): RootState => {
  if (action.type === 'user/clearUser') {
    return appReducer(undefined, action);
  }
  return appReducer(state, action);
};

// Create persisted reducer
const persistedReducer = persistReducer<RootState>(persistConfig, rootReducer);

// Configure store with proper middleware settings
export const store = configureStore({
  reducer: persistedReducer,
  middleware: (getDefaultMiddleware) => {
    return getDefaultMiddleware({
      serializableCheck: {
        ignoredActions: [FLUSH, REHYDRATE, PAUSE, PERSIST, PURGE, REGISTER, 'modals/updateModal'],
        ignoredPaths: ['modals.confirm.onConfirm'],
      },
      immutableCheck: process.env.NODE_ENV === 'development',
    });
  },
  devTools: process.env.NODE_ENV !== 'production',
});

export const persistor = persistStore(store);

export type AppDispatch = typeof store.dispatch;
export type RootState = ReturnType<typeof appReducer>;
export type AppThunk<ReturnType = void> = ThunkAction<
  ReturnType,
  RootState,
  unknown,
  Action<string>
>;
